// ProjectActions — the platform project's ONE registry and its overlays (ADR-036
// §7 — the four kinds; mirrors the exemplar `pages/teams/TeamActions.tsx`).
//
//   • `projectActs` is the PURE registry — rows with a kind, a section and a
//     `show` predicate over the status and the closed-over facts; the shape test
//     (`components/patterns/actionGrammar.test.ts`) walks it.
//   • `useProjectActions` resolves it for a project and returns the ACTS the peek
//     and the page compose from — the peek's top row + door through
//     `ContextPanel`, the page's seat + door through `EntityHeader`, each card's
//     own act through `SectionAct` — and the OVERLAYS: every dialog and drawer,
//     mounted ONCE by the caller beside its surface.
//
// The primary is Open project — a route into the project (the page's seat, the
// peek's main button). The edits, one per card, each named as its drawer's title:
// Edit identity · Manage licensing & add-ons · Edit brand · Manage countries ·
// Manage trial (while the project is on a trial — the Trial card's). The door:
// Suspend (an active project) · Reactivate (one that is not) · Terminate (the
// danger act — the project's code typed to confirm). The whole platform frame is
// the sysadmin's (its route guard), so every row's gate is the constant `admin`
// and the hook's `can` passes it.

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlatformProjectDetail, ProjectStatus } from '@ems-app/schemas/projects/platform-responses';
import { resolveActs, type ActionItem, type EntityAct } from '../../../components/patterns/entityRegistry.js';
import { Alert } from '../../../components/ui/Alert.js';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.js';
import { useToast } from '../../../components/ui/Toast.js';
import { icons } from '../../../components/ui/icons.js';
import { useQuickSave } from '../../../components/patterns/forms/useQuickSave.js';
import { useProjectLifecycle } from '../../../hooks/usePlatformProjectMutations.js';
import { AddonsLicensingDrawer, BrandDrawer, CountriesDrawer, IdentityDrawer, ManageTrialDrawer } from './projectDrawers.js';

/** What each act does — the hook wires these to its overlays. */
export interface ProjectActHandlers {
  editIdentity: () => void;
  manageLicensing: () => void;
  editBrand: () => void;
  manageCountries: () => void;
  manageTrial: () => void;
  suspend: () => void;
  reactivate: () => void;
  terminate: () => void;
}

/** The closed-over facts the rows read beside the status. */
export interface ProjectActFacts {
  /** The project's own frame — Open project's route. */
  projectRoute: string;
  /** On a trial (live or expired) — Manage trial is offered. */
  isTrial: boolean;
}

/** The project's registry — pure: the rows, their kinds, sections and predicates. */
export function projectActs(h: ProjectActHandlers, facts: ProjectActFacts): ActionItem<ProjectStatus>[] {
  return [
    // The primary — the act a sysadmin comes for: into the project (a real route — modifier-click opens a tab).
    { id: 'open-project', kind: 'primary', labelKey: 'actions.openProject', icon: icons.forward, run: { route: facts.projectRoute }, gate: 'admin', order: 10 },
    // The edits — one per card, each the drawer's own title; the peek's Edit menu holds them.
    { id: 'edit-identity', kind: 'edit', section: 'identity', labelKey: 'identityDrawer.title', icon: icons.edit, run: { handler: h.editIdentity }, gate: 'admin', order: 10 },
    { id: 'manage-licensing', kind: 'edit', section: 'licensingAddons', labelKey: 'licensingDrawer.title', icon: icons.license, run: { handler: h.manageLicensing }, gate: 'admin', order: 20 },
    { id: 'manage-trial', kind: 'edit', section: 'trial', labelKey: 'trialDialog.title', icon: icons.calendar, run: { handler: h.manageTrial }, gate: 'admin', order: 30, show: () => facts.isTrial },
    { id: 'edit-brand', kind: 'edit', section: 'brand', labelKey: 'brandDrawer.title', icon: icons.edit, run: { handler: h.editBrand }, gate: 'admin', order: 40 },
    { id: 'manage-countries', kind: 'edit', section: 'countries', labelKey: 'countriesDrawer.title', icon: icons.administrativeArea, run: { handler: h.manageCountries }, gate: 'admin', order: 50 },
    // The door — the lifecycle; Terminate the danger act, fenced last.
    { id: 'suspend', kind: 'lifecycle', labelKey: 'actions.suspend', icon: icons.statusBlocked, run: { handler: h.suspend }, gate: 'admin', order: 10, show: (ctx) => ctx.status === 'active' },
    { id: 'reactivate', kind: 'lifecycle', labelKey: 'actions.reactivate', icon: icons.restore, run: { handler: h.reactivate }, gate: 'admin', order: 10, show: (ctx) => ctx.status !== 'active' },
    { id: 'terminate', kind: 'lifecycle', labelKey: 'actions.terminate', icon: icons.delete, run: { handler: h.terminate }, gate: 'admin', order: 20, tone: 'danger', show: (ctx) => ctx.status !== 'terminated' },
  ];
}

export interface ProjectActions {
  /** The resolved set — what the shells and the cards compose from. */
  acts: EntityAct[];
  /** Every dialog and drawer, mounted ONCE by the caller beside its surface. */
  overlays: ReactNode;
}

type Overlay = 'identity' | 'licensing' | 'trial' | 'brand' | 'countries' | 'suspend' | 'terminate';

// The sysadmin's frame — its route guard is the gate.
const PLATFORM = { can: () => true, entitlements: new Set<never>() };

/** The project's acts. Takes the project while it still loads (the peek keeps one
 *  panel across the load — nothing until it is there). */
export function useProjectActions(project: PlatformProjectDetail | undefined): ProjectActions {
  const { t } = useTranslation('platform-projects');
  const toast = useToast();
  const lifecycle = useProjectLifecycle();
  // Reactivate is low-stakes and direct (no dialog) — a quick save: a refusal said once, the page re-read.
  const quickLifecycle = useQuickSave(lifecycle, { key: 'project-reactivate' });
  // The open overlay, held with the project it was opened for — the peek's hook
  // outlives a peek→peek switch, so an overlay open on one project never reopens on the next.
  const [open, setOpen] = useState<{ overlay: Overlay; projectId: string } | null>(null);

  if (!project) return { acts: [], overlays: null };
  const opened = (overlay: Overlay) => open?.overlay === overlay && open.projectId === project.id;
  const show = (overlay: Overlay) => () => setOpen({ overlay, projectId: project.id });
  const close = () => setOpen(null);

  const reactivate = async (): Promise<void> => {
    if (await quickLifecycle.save({ id: project.id, action: 'reactivate' })) toast.success(t('reactivate.done'));
  };

  const acts = resolveActs(
    projectActs(
      {
        editIdentity: show('identity'),
        manageLicensing: show('licensing'),
        editBrand: show('brand'),
        manageCountries: show('countries'),
        manageTrial: show('trial'),
        suspend: show('suspend'),
        reactivate: () => void reactivate(),
        terminate: show('terminate'),
      },
      { projectRoute: `/p/${project.id}`, isTrial: project.is_trial },
    ),
    { ...PLATFORM, status: project.status },
    (key) => t(key),
  );

  const overlays = (
    <>
      {/* The drawers stay mounted (they slide) — keyed by the project, so a peek→peek switch never keeps the previous seed. */}
      <IdentityDrawer key={`identity:${project.id}`} project={project} open={opened('identity')} onClose={close} />
      <AddonsLicensingDrawer key={`licensing:${project.id}`} project={project} open={opened('licensing')} onClose={close} />
      <BrandDrawer key={`brand:${project.id}`} project={project} open={opened('brand')} onClose={close} />
      <CountriesDrawer key={`countries:${project.id}`} project={project} open={opened('countries')} onClose={close} />
      {project.is_trial && <ManageTrialDrawer key={`trial:${project.id}`} project={project} open={opened('trial')} onClose={close} />}
      <ConfirmDialog
        open={opened('suspend')}
        onClose={close}
        tone="default"
        title={t('suspend.title')}
        description={t('suspend.body')}
        confirmLabel={t('suspend.confirm')}
        onConfirm={async () => {
          await lifecycle.mutateAsync({ id: project.id, action: 'suspend' });
          toast.success(t('suspend.done'));
        }}
      >
        <Alert variant="warning">{t('suspend.memberWarning')}</Alert>
      </ConfirmDialog>
      <ConfirmDialog
        open={opened('terminate')}
        onClose={close}
        tone="danger"
        title={t('terminate.title')}
        confirmLabel={t('terminate.confirm')}
        typeToConfirm={project.name}
        onConfirm={async () => {
          await lifecycle.mutateAsync({ id: project.id, action: 'terminate' });
          toast.success(t('terminate.done'));
        }}
      >
        <Alert variant="danger">{t('terminate.body')}</Alert>
      </ConfirmDialog>
    </>
  );

  return { acts, overlays };
}
